"use client";

import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FormattedMessage } from "react-intl";
import { FaMinus, FaPlus } from "react-icons/fa6";
import { trackCtaClick, trackEvent } from "@/app/providers/ObservabilityProvider";
import { DEMO_URL } from "@/lib/demo";
import { FAQ_QUESTIONS, type FAQQuestionId } from "./faqQuestions";

function FAQItem({
  id,
  open,
  onToggle,
}: {
  id: FAQQuestionId;
  open: boolean;
  onToggle: () => void;
}) {
  const panelId = `faq-panel-${id}`;
  const buttonId = `faq-button-${id}`;

  return (
    <div
      className={`rounded-2xl bg-surface border transition-colors duration-200 ${
        open ? "border-blue-main" : "border-transparent"
      }`}
    >
      <h3>
        <button
          id={buttonId}
          type="button"
          aria-expanded={open}
          // The panel only exists while open (it animates in and out of the DOM).
          aria-controls={open ? panelId : undefined}
          onClick={onToggle}
          className="w-full flex items-start justify-between gap-4 text-left px-5 sm:px-6 py-4 sm:py-5 cursor-pointer"
        >
          <span className="text-sm sm:text-base font-bold text-text-main 2xl:text-lg">
            <FormattedMessage id={`faq.${id}.q`} />
          </span>
          <span
            aria-hidden="true"
            className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-colors duration-200 ${
              open ? "bg-blue-main text-white" : "bg-blue-ulight text-brand"
            }`}
          >
            {open ? (
              <FaMinus className="text-[0.625rem]" />
            ) : (
              <FaPlus className="text-[0.625rem]" />
            )}
          </span>
        </button>
      </h3>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={panelId}
            role="region"
            aria-labelledby={buttonId}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <p className="px-5 sm:px-6 pb-5 text-sm text-gray-main leading-relaxed 2xl:text-base">
              <FormattedMessage id={`faq.${id}.a`} />
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQSection() {
  // Accordion: at most one question open at a time, everything closed initially.
  const [openId, setOpenId] = useState<FAQQuestionId | null>(null);

  // Tracked in the handler, not in the state updater: updaters must stay pure (StrictMode
  // calls them twice, which sent every "open" twice).
  const handleToggle = (id: FAQQuestionId) => {
    const opening = openId !== id;
    if (opening) trackEvent(`faq-${id}-open`, {}, {});
    setOpenId(opening ? id : null);
  };

  return (
    <section id="faq" aria-labelledby="faq-title" className="w-full py-14 px-4 bg-gray-ulight border-t border-gray-light sm:py-20 2xl:py-28">
      <div className="max-w-3xl mx-auto 2xl:max-w-4xl">
        {/* Header */}
        <div className="text-center flex flex-col items-center">
          <h2 id="faq-title" className="text-[2rem] sm:text-5xl font-bold leading-[1.1] tracking-[-0.02em] text-title-blue text-balance 2xl:text-6xl">
            <FormattedMessage id="faq.title" />
            <span className="text-brand">.</span>
          </h2>
          <p className="mt-4 max-w-xl text-base text-text-main text-balance">
            <FormattedMessage id="faq.subtitle" />
          </p>
        </div>

        {/* Questions */}
        <div className="mt-10 flex flex-col gap-3">
          {FAQ_QUESTIONS.map((id) => (
            <FAQItem
              key={id}
              id={id}
              open={openId === id}
              onToggle={() => handleToggle(id)}
            />
          ))}
        </div>

        {/* Still stuck */}
        <div className="mt-6 rounded-2xl bg-surface px-5 sm:px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
          <div className="flex-1">
            <p className="text-sm font-bold text-text-main">
              <FormattedMessage id="faq.more.title" />
            </p>
            <p className="mt-1 text-sm text-gray-main">
              <FormattedMessage id="faq.more.desc" />
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <a
              href="#contact"
              onClick={() => {
                trackEvent("faq-contact-button", {}, {});
                trackCtaClick("faq", "contact");
              }}
              className="text-center bg-blue-ulight text-brand-ink font-semibold text-sm px-6 py-3 rounded-full hover:bg-blue-light transition-colors duration-200"
            >
              <FormattedMessage id="faq.more.contact" />
            </a>
            <a
              href={DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackEvent("faq-demo-button", {}, { criticalOnNav: true });
                trackCtaClick("faq", "demo");
              }}
              className="text-center bg-blue-main text-white font-semibold text-sm px-6 py-3 rounded-full hover:bg-[#4F46E5] transition-colors duration-200"
            >
              <FormattedMessage id="faq.more.demo" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
